<template>
  <base-draggable-modal
      :name="modal_name"
      max-height="80vh"
      width="50vw"
      :fullscreen="$vuetify.display.xsOnly"
      :ref="modal_name">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title v-if="label" class="text-primary">{{ label }}</v-toolbar-title>
            <v-toolbar-title v-else class="text-primary">{{ $store.getters.translate(page.model) }}</v-toolbar-title>
            <v-spacer/>
            <base-action-button v-if="canDelete" class="mr-5" :delete="true" @action="archive">
              <template #prepend><PhArchive style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
              {{ $store.getters.translate("delete") }}
            </base-action-button>
          </v-toolbar>
        </template>
        <template #content>
            <v-card-text>
              <!--USUAL FIELDS-->
              <base-field
                  v-for="field in usual_fields"
                  :key="field.name"
                  :isDisabled="!canEdit"
                  :modelValue="record[field.name]"
                  :field="field"
                  @change="usualFieldDataChanged"/>
              <!--LIST FILES-->
              <v-row v-if="files_required" class="mt-5">
                <v-col>
                  <base-list-files
                      :page="page"
                      :parent_id="record.id"
                      :label="$store.getters.translate('files')"
                      :files="record.files"
                      :multiple_files="true"
                      :deletable="canEdit"
                      :create="canEdit"
                      @refresh="load(record.id)"/>
                </v-col>
              </v-row>
              <v-row v-if="addresses_required" class="mt-0">
                <v-col>
                  <base-addresses :isDisabled="!canEdit" v-model="record.addresses" address_type="visiting" label="contact_address" @update="addressesChanged"/>
                </v-col>
              </v-row>
              <v-row v-if="email_addresses_required" class="mt-0">
                <v-col>
                  <base-email-addresses :isDisabled="!canEdit" v-model="record.emailaddresses" email_type="personal" label="emailaddress" @update="emailsChanged"/>
                </v-col>
              </v-row>
              <v-row v-if="page.model === 'note'" class="mt-0">
                <v-col>
                  <template v-if="record.candidates && record.candidates.length > 0">
                    <span class="reg-normal text-primary ml-3">{{ $store.getters.translate("candidates") }}</span>
                    <br/>
                    <v-chip
                        v-for="candidate in record.candidates"
                        :key="candidate.id"
                        @click="$router.push('/candidates/' + candidate.id)"
                        class="mt-3"
                        color="primary"
                        size="small">
                      <span class="mr-1"> {{ candidate.name }}</span>
                      <small>({{ $store.getters.translate("candidate_id") }}: {{ candidate.id }})</small>
                    </v-chip>
                    <br/>
                  </template>
                  <template v-if="record.assessors && record.assessors.length > 0">
                    <span class="reg-normal text-primary ml-3">{{ $store.getters.translate("assessors") }}</span>
                    <br/>
                    <v-chip
                        v-for="assessor in record.assessors"
                        :key="assessor.id"
                        @click="$router.push('/assessors/' + assessor.id)"
                        class="mt-3"
                        color="primary"
                        size="small">
                      <span class="mr-1"> {{ assessor.name }}</span>
                      <small>({{ $store.getters.translate("assessor_id") }}: {{ assessor.id }})</small>
                    </v-chip>
                    <br/>
                  </template>
                  <template v-if="record.companies && record.companies.length > 0">
                    <span class="reg-normal text-primary ml-3">{{ $store.getters.translate("companies") }}</span>
                    <br/>
                    <v-chip
                        v-for="company in record.companies"
                        :key="company.id"
                        @click="$router.push('/companies/' + company.id)"
                        class="mt-3"
                        color="primary"
                        size="small">
                      <span class="mr-1"> {{ company.name }}</span>
                      <small>({{ $store.getters.translate("company_id") }}: {{ company.id }})</small>
                    </v-chip>
                    <br/>
                  </template>
                  <template v-if="record.exams && record.exams.length > 0">
                    <span class="reg-normal text-primary ml-3">{{ $store.getters.translate("exams") }}</span>
                    <br/>
                    <v-chip
                        v-for="exam in record.exams"
                        :key="exam.id"
                        @click="$router.push('/exams/' + exam.id)"
                        class="mt-3"
                        color="primary"
                        size="small">
                      <span class="mr-1"> {{ exam.name }}</span>
                      <small>({{ $store.getters.translate("exam") }}: {{ exam.id }})</small>
                    </v-chip>
                    <br/>
                  </template>
                  <template v-if="record.dossiers && record.dossiers.length > 0">
                    <span class="reg-normal text-primary ml-3">{{ $store.getters.translate("dossiers") }}</span>
                    <br/>
                    <v-chip
                        v-for="dossier in record.dossiers"
                        :key="dossier.id"
                        @click="$router.push('/dossiers/' + dossier.id)"
                        class="mt-3"
                        color="primary"
                        size="small">
                      <span class="mr-1"> {{ dossier.name }}</span>
                      <small>({{ $store.getters.translate("dossier") }}: {{ dossier.id }})</small>
                    </v-chip>
                    <br/>
                  </template>
                  <template v-if="record.schemes && record.schemes.length > 0">
                    <span class="reg-normal text-primary ml-3">{{ $store.getters.translate("schemes") }}</span>
                    <br/>
                    <v-chip
                        v-for="scheme in record.schemes"
                        :key="scheme.id"
                        @click="$router.push('/schemes/' + scheme.id)"
                        class="mt-3"
                        color="primary"
                        size="small">
                      <span class="mr-1"> {{ scheme.name }}</span>
                      <small>({{ $store.getters.translate("scheme") }}: {{ scheme.id }})</small>
                    </v-chip>
                  </template>
                </v-col>
              </v-row>
              <v-row v-if="attach_to_required" class="mt-0">
                <v-col>
                  <v-autocomplete
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="attach_to_selected_models"
                      :items="['Candidates', 'Exams', 'Dossiers']"
                      :return-object="false"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('attach_to') }}</span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                      v-if="attach_to_selected_models.includes('Candidates')"
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="record.candidate_ids"
                      :items="candidates"
                      :return-object="false"
                      item-value="id"
                      item-title="name"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('candidates') }}</span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                      v-if="attach_to_selected_models.includes('Exams')"
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="record.exam_ids"
                      :items="exams"
                      :return-object="false"
                      item-value="id"
                      item-title="name"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('exams') }}</span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                      v-if="attach_to_selected_models.includes('Dossiers')"
                      density="default"
                      class="reg-normal text-primary mt-3"
                      base-color="border-tertiary"
                      bg-color="secondary"
                      color="primary"
                      v-model="record.dossier_ids"
                      :items="dossiers"
                      :return-object="false"
                      item-value="id"
                      item-title="name"
                      hide-details
                      clearable
                      multiple>
                    <template #label>
                      <span class="sm-normal text-subtle">{{ $store.getters.translate('dossiers') }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" :isDisabled="!canEdit" :modelValue="record.tags" :modal_name="page.name" :label="$store.getters.translate('tags')" @change="tagsChanged"/>
            </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="closeModal(modal_name)">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("close") }}
                </base-action-button>
              </v-col>
              <v-col v-if="canEdit">
                <base-action-button :background="true" :width="'100%'" @action="save(true)">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";
import BaseTags from "./BaseTags";
import BaseAddresses from "./BaseAddresses";
import BaseEmailAddresses from "./BaseEmailAddresses";
import BaseListFiles from "./BaseListFiles";
import BaseActionButton from "./BaseActionButton";
import BaseLinkButton from "./BaseLinkButton";
import {PhArchive, PhArrowSquareOut, PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ["modal_name", "page", "label", "addresses_required", "email_addresses_required", "files_required", "multiple_files", "attach_to_required"],
  components: {
    BaseEmailAddresses,
    BaseAddresses,
    BaseLinkButton,
    BaseModal,
    BaseDraggableModal,
    BaseTags,
    BaseListFiles,
    BaseActionButton,
    PhArrowSquareOut, PhArchive, PhFloppyDisk, PhX,
  },
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      record: {},
      usual_fields: [],
      tags_required: false,
      //NOTES VARIABLES
      attach_to_selected_models: [],
      candidates: [],
      exams: [],
      dossiers: [],
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load(id) {
      this.usual_fields = [];
      this.openModal(this.modal_name);
      this.record = await helpFunctions.loadModel(this.$toast, this.page.name, id);
      await this.getFields();
      if(this.page.model === 'tag') {
        this.record.name = this.record.name.en;
      }
      else if(this.page.model === 'note' && this.attach_to_required) {
        this.record.candidate_ids = this.$lodash.map(this.record.candidates, "id");
        this.record.exam_ids = this.$lodash.map(this.record.exams, "id");
        this.record.dossier_ids = this.$lodash.map(this.record.dossiers, "id");
      }
    },
    async save(reload) {
      if (helpFunctions.modelFieldsFilled(this.$toast, this.record, this.usual_fields)) {
        if(await helpFunctions.saveModel(this.$toast, this.page.name, this.record)) {
          if(this.page.model === 'translation') {
            await this.$store.dispatch("refreshTranslations");
          }
          if (reload) {
            this.$emit('refresh');
            this.closeModal(this.modal_name);
          }
        }
      }
    },
    async archive() {
      this.$confirm(this.$store.getters.translate("confirm")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, this.record.id);
          this.$emit('refresh');
          this.closeModal(this.modal_name);
        }
      });
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
      }
    },
    emailsChanged(updated_emails) {
      this.record.emailaddresses = updated_emails;
      this.save(false);
    },
    addressesChanged(updated_addresses) {
      this.record.addresses = updated_addresses;
    },
    tagsChanged(data) {
      this.record.tags = data;
    },
    async getFields() {
      const response = await helpFunctions.getFields(this.page.model, "show_on_update");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
    },
  },
  computed: {
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
};
</script>